/* eslint-disable max-len */
import { Col, Row } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { AxiosResponse } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import AuthWrapper from 'components/base/AuthWrapper';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import LoginCollapse from 'components/molecules/LoginCollapse';

import useProfily from 'hooks/useProfily';

import pristupPlatnost from 'services/akcionar/pristupPlatnost';
import { ETypPrihlaseni } from 'services/profily';

import { EPristupPlatnost } from 'enums/pristupPlatnost';
import routePathEnum from 'enums/routePathEnum';

interface ILogin {}

const LoginPageTexts = {
    LEI: {
        title: 'LEI_LOGIN_TITLE',
        text: 'LEI_PRIHLASENI_SUB1'
    },
    EMITENT: {
        title: 'ROZCESTNIK_LOGIN_EMITENT_TITLE',
        text: 'ROZCESTNIK_LOGIN_EMITENT_TEXT'
    },
    SRD: {
        title: 'ROZCESTNIK_LOGIN_SRD_TITLE',
        text: 'ROZCESTNIK_LOGIN_SRD_TEXT'
    },
    STATNISPRAVA: {
        title: 'ROZCESTNIK_LOGIN_STATNISPRAVA_TITLE',
        text: 'ROZCESTNIK_LOGIN_STATNISPRAVA_TEXT'
    },
    JINAOSOBA: {
        title: 'JINA_OSOBA',
        text: 'JINA_OSOBA_TEXT'
    }
};

const Login: React.FC<ILogin> = () => {
    const navigate = useNavigate();
    const { language, prevPath } = useContext(Context);
    const { tokenURL } = useParams<{ tokenURL: string }>();
    const { setLevel } = useProfily();
    const [form] = useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [akcionarLoading, setAkcionarLoading] = useState<boolean>(false);
    const [akcionar, setAkcionar] = useState<number>(0);
    const [akcionarLogin, setAkcionarLogin] = useState<string>('');
    const [akcionarCantLogin, setAkcionarCantLogin] = useState<boolean>(false);

    const loginPage: 'LEI' | 'EMITENT' | 'SRD' | 'STATNISPRAVA' | 'JINAOSOBA' | undefined =
        window.location.hash.includes(routePathEnum.LEI_LOGIN_MUJDEP)
            ? 'LEI'
            : window.location.hash.includes(routePathEnum.EMITENT_LOGIN_MUJDEP) ||
                window.location.hash.includes(routePathEnum.LOGIN)
              ? 'EMITENT'
              : window.location.hash.includes(routePathEnum.SRD_LOGIN_MUJDEP)
                ? 'SRD'
                : window.location.hash.includes(routePathEnum.STATNISPRAVA_LOGIN_MUJDEP)
                  ? 'STATNISPRAVA'
                  : window.location.hash.includes(routePathEnum.LOGIN_JINA_OSOBA)
                    ? 'JINAOSOBA'
                    : undefined;

    const loginOptions = {
        LEI: [ETypPrihlaseni.UZIV, ETypPrihlaseni.CERT, ETypPrihlaseni.BI],
        EMITENT: [ETypPrihlaseni.UZIV, ETypPrihlaseni.CERT, ETypPrihlaseni.BI],
        SRD: [ETypPrihlaseni.UZIV, ETypPrihlaseni.CERT, ETypPrihlaseni.BI],
        STATNISPRAVA: [ETypPrihlaseni.CERT],
        JINAOSOBA: [ETypPrihlaseni.UZIV, ETypPrihlaseni.CERT]
    };

    useEffect(() => {
        if (!tokenURL) return;
        setAkcionarLoading(true);
        setLevel('tokenURL', tokenURL);
        pristupPlatnost(tokenURL)
            .then((resp: AxiosResponse) => {
                setAkcionar(resp.status);
                if (resp.data.stav === EPristupPlatnost.REGISTRACE) {
                    setLevel('profily', {
                        id: resp.data.id,
                        jmeno: resp.data.jmeno,
                        prijmeni: resp.data.prijmeni,
                        email: resp.data.email,
                        email2: resp.data.email,
                        telefon1: resp.data.telefon,
                        telefon2: resp.data.telefon
                    });
                    navigate(routePathEnum.REGISTRACE);
                }
                if (resp.data.stav === EPristupPlatnost.LOGIN) {
                    setAkcionarLogin(resp.data.email);
                    form.setFieldsValue({
                        username: resp.data.email
                    });
                }
                if (resp.data.stav === EPristupPlatnost.NELZE) {
                    setAkcionarCantLogin(true);
                }
            })
            .catch((err: any) => {
                setAkcionar(err.response ? err.response.status : 401);
            })
            .finally(() => setAkcionarLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenURL]);

    /** Clean up akce po prihlaseni */
    useEffect(() => {
        return () => setLoading(false);
    }, []);

    return (
        <AuthWrapper>
            <LoadingWrapper loading={akcionarLoading || loading}>
                {(akcionar === 200 || akcionar === 401 || akcionar === 400) && (
                    <>
                        <Title text="AKCIONAR_PRIPOJENI" level={2} />
                        {akcionar === 200 && <Paragraph text="AKCIONAR_PRIPOJENI_TXT" />}
                        {akcionar === 401 && <Paragraph text="AKCIONAR_PRIPOJENI_401" />}
                        {akcionar === 400 && <Paragraph text="AKCIONAR_PRIPOJENI_400" />}
                    </>
                )}
                {akcionarCantLogin && (
                    <>
                        <Title text="LOGIN_WRONG" level={4} /> <Paragraph text="LOGIN_WRONG" />
                    </>
                )}
                {!(akcionar === 401 || akcionar === 400 || akcionarCantLogin) && (
                    <>
                        {loginPage && (
                            <>
                                <Title
                                    text={LoginPageTexts[loginPage].title}
                                    level={1}
                                    style={
                                        akcionarLogin
                                            ? {
                                                  position: 'relative',
                                                  bottom: -26
                                              }
                                            : {}
                                    }
                                />
                                <Paragraph
                                    text={LoginPageTexts[loginPage].text}
                                    style={{
                                        paddingBottom: 8,
                                        display: 'block'
                                    }}
                                />

                                {loginPage === 'LEI' && (
                                    <Row
                                        style={{
                                            paddingTop: 10,
                                            paddingBottom: 10
                                        }}
                                    >
                                        <Col
                                            style={{
                                                marginRight: 30
                                            }}
                                        >
                                            <AnchorComponent
                                                id="loginNavodBtn"
                                                href={
                                                    language === 'cz'
                                                        ? 'https://www.cdcp.cz/wp-content/uploads/Navod_prideleni_LEI.pdf'
                                                        : 'https://www.cdcp.cz/wp-content/uploads/LEI_Assignment.pdf'
                                                }
                                                target="_blank"
                                            >
                                                <TranslateText>LOGIN_NAVOD_LEI_PRIDELENI</TranslateText>
                                            </AnchorComponent>
                                        </Col>
                                        <Col>
                                            <AnchorComponent
                                                id="loginAktualizaceBtn"
                                                href={
                                                    language === 'cz'
                                                        ? 'https://www.cdcp.cz/wp-content/uploads/Navod_proudlouzeni_LEI.pdf'
                                                        : 'https://www.cdcp.cz/wp-content/uploads/Update_LEI-LEI_validity_extension.pdf'
                                                }
                                                target="_blank"
                                            >
                                                <TranslateText>LOGIN_NAVOD_LEI_AKTUALIZACE</TranslateText>
                                            </AnchorComponent>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        )}
                        <Title
                            text="PRIHLASENI"
                            level={loginPage ? 2 : 1}
                            style={
                                akcionarLogin
                                    ? {
                                          position: 'relative',
                                          bottom: -26
                                      }
                                    : {}
                            }
                        />

                        <>
                            {!akcionarLogin && (
                                <>
                                    <TranslateText>EMP_LOGIN_PODNADPIS_MOZNOSTI</TranslateText>
                                    <br />
                                </>
                            )}
                            <LoginCollapse
                                loginOptions={loginPage ? loginOptions[loginPage] : []}
                                setLoading={setLoading}
                                akcionar={akcionar}
                                akcionarLogin={akcionarLogin}
                            />
                        </>
                        {!akcionarLogin && loginPage !== 'STATNISPRAVA' && (
                            <Row>
                                {loginPage !== 'LEI' && <TranslateText>REGISTROVAT_BUTTON_TEXT</TranslateText>}
                                <ButtonComponent
                                    id="registrovatBtn"
                                    style={{
                                        marginTop: -8,
                                        marginLeft: 8
                                    }}
                                    htmlType="button"
                                    type="primary"
                                    buttontext={loginPage === 'LEI' ? 'LEI_LOGIN_REGISTER' : 'REGISTROVAT_BUTTON'}
                                    onClick={() => {
                                        setLevel('prevPath', window.location.hash);
                                        navigate(routePathEnum.REGISTRACE);
                                    }}
                                />
                            </Row>
                        )}
                        {loginPage && (
                            <ButtonComponent
                                style={{
                                    marginTop: 8
                                }}
                                htmlType="button"
                                type="default"
                                id="leiPrihlaseniBtn"
                                buttontext="ZPET"
                                onClick={() =>
                                    loginPage === 'LEI' && !prevPath.includes(routePathEnum.LOGIN_ROZCESTNIK)
                                        ? window.location.replace(
                                              window.location.href.includes('test')
                                                  ? 'https://test.cdcp.cz/lei/sluzby-lei/'
                                                  : 'https://www.cdcp.cz/lei/sluzby-lei/'
                                          )
                                        : navigate(routePathEnum.LOGIN_ROZCESTNIK)
                                }
                            />
                        )}
                    </>
                )}
            </LoadingWrapper>
        </AuthWrapper>
    );
};

export default Login;
